"use client";
import React, { useState } from "react";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import GetInTouch from "@/components/modals/get-in-touch";
import HeroDivider from "@/components/ui/hero-divider";
import { useTranslations } from "next-intl";

const blobA = {
  animate: {
    x: [0, 40, -20, 0] as number[],
    y: [0, -30, 20, 0] as number[],
    scale: [1, 1.08, 0.95, 1] as number[],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const blobB = {
  animate: {
    x: [0, -50, 30, 0] as number[],
    y: [0, 40, -20, 0] as number[],
    scale: [1, 0.92, 1.06, 1] as number[],
    transition: { duration: 15, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
} as const;


const ServicesHero = () => {
  const t = useTranslations("services");
  const [isGetInTouchOpen, setIsGetInTouchOpen] = useState(false);

  return (
    <section className="section pt-[120px] md:pt-[160px] xl:pt-[200px] pb-0 relative overflow-hidden">
      <div className="absolute top-[5%] left-[10%] w-[600px] h-[600px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.25)_0%,_rgba(135,82,250,0)_100%)] rounded-full blur-[120px] pointer-events-none animate-blob-a" />
      <div className="absolute bottom-[-10%] right-[5%] w-[400px] h-[400px] bg-[linear-gradient(180deg,_rgba(98,42,173,0.2)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[90px] pointer-events-none animate-blob-b" />

      <div className="container relative z-10 pb-[70px] lg:pb-[110px]">
        <motion.div
          className="max-w-[800px] mx-auto text-center"
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          <motion.h1
            className="text-[30px] sm:text-[38px] md:text-[50px] xl:text-[60px] font-semibold leading-[1.2] mb-[24px]"
            variants={itemVariants}
          >
            {t("heroTitle")}<br />{t("heroTitleLine2")}
          </motion.h1>

          <motion.p
            className="text-[16px] lg:text-[18px] font-light leading-[1.7] text-foreground/90 max-w-[580px] mx-auto mb-[40px]"
            variants={itemVariants}
          >
            {t("heroDescription")}
          </motion.p>

          <motion.div
            className="flex flex-row flex-wrap items-center gap-[10px] justify-center"
            variants={itemVariants}
          >
            <Button
              variant="theme"
              className="h-[48px] rounded-[14px] px-[16px] md:px-[28px] text-[14px] md:text-[16px] shadow-none cursor-pointer"
              onClick={() => setIsGetInTouchOpen(true)}
            >
              {t("startProject")}
            </Button>
            <Button
              variant="secondary"
              className="h-[48px] rounded-[14px] px-[16px] md:px-[28px] text-[14px] md:text-[16px] shadow-none cursor-pointer"
              onClick={() => {
                document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              {t("exploreServices")}
            </Button>
          </motion.div>
        </motion.div>
      </div>


      <GetInTouch open={isGetInTouchOpen} onOpenChange={setIsGetInTouchOpen} />

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-[80px] lg:h-[120px] bg-gradient-to-b from-transparent to-background pointer-events-none z-10" />

      <HeroDivider />
    </section>
  );
};

export default ServicesHero;
